import {useMapsLibrary} from "@vis.gl/react-google-maps";
import {useEffect, useRef, useState} from "react";
import {ChevronRight, MapPin} from "lucide-react";
import {actions, useApp} from "../providers/AppProvider.tsx";
import clsx from "clsx";

interface PlaceAutocompleteProps {
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
}


function selectFirstPlacePrediction(input: HTMLInputElement) {
    // Create and dispatch a "keydown" event for the "Down Arrow" key
    const downArrowEvent = new KeyboardEvent('keydown', {
        key: 'ArrowDown',
        code: 'ArrowDown',
        keyCode: 40,
        bubbles: true
    });
    input.dispatchEvent(downArrowEvent);

    // Create and dispatch a "keydown" event for the "Enter" key
    const enterEvent = new KeyboardEvent('keydown', {
        key: 'Enter',
        code: 'Enter',
        keyCode: 13,
        bubbles: false
    });
    input.dispatchEvent(enterEvent);
}

const PlaceAutocomplete = ({onPlaceSelect}: PlaceAutocompleteProps) => {
    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const places = useMapsLibrary('places');
    const geocoder = useMapsLibrary('geocoding');

    const {dispatch, state} = useApp()

    useEffect(() => {
        if (!places || !inputRef.current) return;

        const options: google.maps.places.AutocompleteOptions = {
            fields: ['geometry', 'formatted_address'],
            componentRestrictions: {
                country: ['US', 'PR', 'ATG']
            }
        };

        setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
    }, [places]);

    // Sync inputValue with actual input element value
    useEffect(() => {
        if (!inputRef.current) return;

        const handleInput = () => {
            setInputValue(inputRef.current?.value || '');
        };

        inputRef.current.addEventListener('input', handleInput);
        return () => {
            inputRef.current?.removeEventListener('input', handleInput);
        };
    }, []);
    const handleKeyPress = (event: KeyboardEvent) => {

        if (event.key === 'Enter') {
            console.log('Enter key pressed!');

            // Wait briefly for predictions to appear before simulating keypresses
            const searchInput = document.querySelector<HTMLInputElement>('#search')
            if (searchInput) {
                setTimeout(() => {
                    selectFirstPlacePrediction(searchInput);
                }, 100);
            }
        }


    };
    useEffect(() => {

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, []);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener('place_changed', () => {
            onPlaceSelect(placeAutocomplete.getPlace());
            dispatch(actions.setSelectedPractice(undefined));
            setIsFocused(false);
        });
    }, [dispatch, onPlaceSelect, placeAutocomplete]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current && !inputRef.current.contains(event.target as Node)) {
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleUseCurrentLocation = async () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        setIsLoadingLocation(true);

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const {latitude, longitude} = position.coords;

                // Create a PlaceResult-like object with the user's current location
                const location = new google.maps.LatLng(latitude, longitude);

                // Use geocoder to get formatted address if available
                if (geocoder) {
                    const geocoderInstance = new geocoder.Geocoder();
                    geocoderInstance.geocode({location}, (results, status) => {
                        if (status === 'OK' && results && results[0]) {
                            const placeResult: google.maps.places.PlaceResult = {
                                geometry: {
                                    location: location,
                                } as google.maps.places.PlaceGeometry,
                                formatted_address: results[0].formatted_address
                            };

                            onPlaceSelect(placeResult);
                            dispatch(actions.setSelectedPractice(undefined));

                            if (inputRef.current) {
                                inputRef.current.value = results[0].formatted_address || 'Current Location';
                                setInputValue(inputRef.current.value);
                            }
                        } else {
                            // Fallback without formatted address
                            const placeResult: google.maps.places.PlaceResult = {
                                geometry: {
                                    location: location,
                                } as google.maps.places.PlaceGeometry,
                                formatted_address: 'Current Location'
                            };

                            onPlaceSelect(placeResult);
                            dispatch(actions.setSelectedPractice(undefined));

                            if (inputRef.current) {
                                inputRef.current.value = 'Current Location';
                                setInputValue(inputRef.current.value);
                            }
                        }

                        setIsLoadingLocation(false);
                        setIsFocused(false);
                    });
                } else {
                    // Fallback if geocoder is not available
                    const placeResult: google.maps.places.PlaceResult = {
                        geometry: {
                            location: location,
                        } as google.maps.places.PlaceGeometry,
                        formatted_address: 'Current Location'
                    };

                    onPlaceSelect(placeResult);
                    dispatch(actions.setSelectedPractice(undefined));

                    if (inputRef.current) {
                        inputRef.current.value = 'Current Location';
                        setInputValue(inputRef.current.value);
                    }

                    setIsLoadingLocation(false);
                    setIsFocused(false);
                }
            },
            (error) => {
                console.error('Error getting location:', error);
                alert('Unable to retrieve your location. Please check your browser permissions.');
                setIsLoadingLocation(false);
                setIsFocused(false);
            }
        );
    };

    return (
        <div
            className={clsx({
                "autocomplete-container absolute top-0 py-3 pl-6 pr-7 right-0 left-0 h-[65px]": !state.isMobileDevice,
                'py-3 pl-4 pr-7': state.isMobileDevice
            })}>
            <form className="relative" onSubmit={(ev) => ev.preventDefault()}>
                <input ref={inputRef}
                       type={"search"}
                       autoComplete={"off"}
                       placeholder="Enter City, State or Zip"
                       className={"bg-white w-full block border-green-600 border-2 rounded-md py-1 px-2 leading-8 focus:outline-green-100 focus:outline-3 focus:outline-offset-1"}
                       id={"search"}
                       onFocus={() => setIsFocused(true)}/>
                <button type={'submit'}
                        className={"cursor-pointer hover:bg-green-700 p-0.5 flex items-center justify-center bg-green-600 rounded-full absolute -right-4 top-1/2 -translate-y-1/2"}>
                    <ChevronRight color={"#fff"} size={40}/>
                </button>

                {/* Current Location Dropdown */}
                {isFocused && inputValue === '' && (
                    <div
                        ref={dropdownRef}
                        className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-green-600 rounded-md shadow-lg z-50">
                        <button
                            type="button"
                            onClick={handleUseCurrentLocation}
                            disabled={isLoadingLocation}
                            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-green-50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed">
                            <MapPin size={20} className="text-green-600"/>
                            <span className="text-gray-800">
                                {isLoadingLocation ? 'Getting your location...' : 'Use current location'}
                            </span>
                        </button>
                    </div>
                )}
            </form>

        </div>
    );
};

export default PlaceAutocomplete;